'use strict';

import { notification } from '../utils/notification';
import { sharedStorage } from '../utils/sharedStorage';
import { tabIdentity } from '../utils/tabIdentity';

/**
 * Cross-tab attention: identify titles, blink, and activate.
 *
 * **Purpose:** Help agents/devtools find and nudge a specific storefront tab
 * (by Client Tab Id) without changing checkout ownership or CXP write locks.
 *
 * **Scope:**
 * - Identify mode: show/hide tab id in `document.title` (persisted via
 *   `IDENTIFY_KEY` / `setIdentify` / `applyIdentify`)
 * - Remote/local title blink (`requestBlink` / `blinkTitle` / `stopTitleBlink`)
 * - Activate = blink + optional Web Notification (`requestActivate`)
 * - Pending ack maps for cross-tab delivery (`pendingActivates` / `pendingBlinks`)
 *
 * **Out of scope:** Minting Client Tab Id (`tabIdentity`); bus subscribe (global
 * `components/coordinator`); Save Execution Gate; tab presence / tab reconcile;
 * CSRF recovery.
 */
export const tabAttention = {
  /**
   * @readonly
   * @private
   */
  STATE_KEY: 'attention',

  /**
   * @readonly
   * @private
   */
  IDENTIFY_KEY: 'honda_multi_cart_identify',

  /**
   * @readonly
   * @private
   */
  ACTIVATE_TIMEOUT: 800,

  /**
   * @private
   *
   * @returns {{
   *   initialized: boolean,
   *   pendingActivates: Record<string, Function>,
   *   pendingBlinks: Record<string, Function>,
   *   originalTitle: string|null,
   *   identifyEnabled: boolean,
   *   isTitleBlinking: boolean,
   *   blinkInterval: number|null,
   *   blinkDurationTimer: number|null,
   *   blinkVisibilityHandler: Function|null,
   *   blinkOriginalTitle: string|null,
   *   blinkIdleTitle: string|null
   * }}
   */
  get state() {
    return sharedStorage.get(this.STATE_KEY, () => ({
      initialized: false,
      pendingActivates: {},
      pendingBlinks: {},
      originalTitle: null,
      identifyEnabled: false,
      isTitleBlinking: false,
      blinkInterval: null,
      blinkDurationTimer: null,
      blinkVisibilityHandler: null,
      blinkOriginalTitle: null,
      blinkIdleTitle: null
    }));
  },

  /**
   * Whether peer bus handlers for this module have been registered.
   *
   * @returns {boolean}
   */
  isInitialized() {
    return !!this.state.initialized;
  },

  /**
   * Mark peer bus handlers as registered (idempotent wirer guard).
   */
  setInitialized() {
    this.state.initialized = true;
  },

  /**
   * Whether tab-id document titles are currently enabled (persisted in localStorage).
   *
   * @returns {boolean}
   */
  isIdentifyEnabled() {
    try {
      return localStorage.getItem(this.IDENTIFY_KEY) === '1';
    } catch (e) {
      return !!this.state.identifyEnabled;
    }
  },

  /**
   * Toggle tab-id document titles across all open storefront tabs.
   *
   * @param {boolean} enabled
   */
  setIdentify(enabled) {
    enabled = !!enabled;

    try {
      if (enabled) {
        localStorage.setItem(this.IDENTIFY_KEY, '1');
      } else {
        localStorage.removeItem(this.IDENTIFY_KEY);
      }
    } catch (e) {
      // Best-effort persistence for new tabs / refresh.
    }

    this.applyIdentify(enabled);
    sharedStorage.tabChannel.publish('identify', { enabled: enabled });
  },

  /**
   * Ask another storefront tab to blink its document title (no notification).
   *
   * @param {string} tabId
   * @param {{
   *   title?: string,
   *   idleTitle?: string,
   *   prefix?: string,
   *   intervalMs?: number,
   *   duration?: number,
   *   count?: number,
   *   timeout?: number
   * }} [options]
   *
   * @returns {Promise<{
   *   delivered: boolean,
   *   timedOut?: boolean,
   *   self?: boolean,
   *   status?: object|null
   * }>}
   */
  requestBlink(tabId, options = {}) {
    const { title, timeout = this.ACTIVATE_TIMEOUT, ...blinkOptions } = options || {};
    const state = this.state;

    if (!tabId) {
      return Promise.resolve({ delivered: false, timedOut: false, status: null });
    }

    if (tabId === tabIdentity.peekTabId()) {
      return Promise.resolve({
        delivered: true,
        self: true,
        status: this.blinkTitle(title, blinkOptions)
      });
    }

    return new Promise(resolve => {
      const requestNonce = Math.random().toString(36).slice(2);

      state.pendingBlinks[requestNonce] = resolve;

      sharedStorage.tabChannel.publish('blink', {
        tabId: tabId,
        options: options,
        requestNonce: requestNonce
      });

      window.setTimeout(() => {
        if (!state.pendingBlinks[requestNonce]) {
          return;
        }

        delete state.pendingBlinks[requestNonce];
        resolve({ delivered: false, timedOut: true, status: null });
      }, timeout);
    });
  },

  /**
   * Ask another storefront tab to draw attention (title blink + optional
   * Web Notification). Resolves with delivery / permission status from the
   * target tab so the caller can fall back when notifications are blocked.
   *
   * @param {string} tabId
   * @param {{ title?: string, message?: string, timeout?: number }} [options]
   *
   * @returns {Promise<{
   *   delivered: boolean,
   *   timedOut?: boolean,
   *   self?: boolean,
   *   status?: object|null
   * }>}
   */
  requestActivate(tabId, options) {
    const opts = options || {};
    const title = opts.title || 'Honda Cars';
    const message = opts.message || '';
    const timeout = opts.timeout || this.ACTIVATE_TIMEOUT;
    const state = this.state;

    if (!tabId) {
      return Promise.resolve({ delivered: false, timedOut: false, status: null });
    }

    if (tabId === tabIdentity.peekTabId()) {
      const blink = this.blinkTitle(title);
      const status = Object.assign(
        { titleBlinking: blink.titleBlinking },
        notification.notify(title, {
          body: message,
          onClick: () => window.focus(),
          onClose: () => this.stopTitleBlink(),
          onError: () => this.stopTitleBlink()
        }, () => {
          window.focus();
          this.stopTitleBlink();
        })
      );

      return Promise.resolve({
        delivered: true,
        self: true,
        status: status
      });
    }

    return new Promise(resolve => {
      const requestNonce = Math.random().toString(36).slice(2);

      state.pendingActivates[requestNonce] = resolve;

      sharedStorage.tabChannel.publish('activate', {
        tabId: tabId,
        title: title,
        message: message,
        requestNonce: requestNonce
      });

      window.setTimeout(() => {
        if (!state.pendingActivates[requestNonce]) {
          return;
        }

        delete state.pendingActivates[requestNonce];
        resolve({ delivered: false, timedOut: true, status: null });
      }, timeout);
    });
  },

  /**
   * Whether the document title is currently blinking.
   *
   * @returns {boolean}
   */
  isTitleBlinking() {
    return !!this.state.isTitleBlinking;
  },

  /**
   * Blink `document.title` while this tab is hidden. Stops when the tab
   * becomes visible, or when `duration` / `count` elapses.
   *
   * @param {string} title - Alert text shown while blinking.
   * @param {{
   *   title?: string,
   *   prefix?: string,
   *   suffix?: string,
   *   intervalMs?: number,
   *   duration?: number,
   *   count?: number
   * }} [options]
   *
   * @returns {{ titleBlinking: boolean }}
   */
  blinkTitle(title = document.title, options = {}) {
    const {
      prefix = '',
      suffix = '',
      intervalMs = 1000,
      count,
      duration
    } = options;
    const state = this.state;
    const alertText = prefix + title + suffix;

    this.stopTitleBlink();

    if (!document.hidden) {
      return { titleBlinking: false };
    }

    state.blinkOriginalTitle = document.title;
    state.blinkIdleTitle = state.blinkOriginalTitle;
    state.isTitleBlinking = true;

    let isBlinking = false;
    let flashCount = 0;

    state.blinkInterval = window.setInterval(() => {
      if (!document.hidden) {
        this.stopTitleBlink();
        return;
      }

      isBlinking = !isBlinking;
      document.title = isBlinking ? alertText : state.blinkIdleTitle;

      if (isBlinking && count) {
        flashCount += 1;

        if (flashCount > count) {
          this.stopTitleBlink();
        }
      }
    }, intervalMs);

    if (duration) {
      state.blinkDurationTimer = window.setTimeout(() => {
        this.stopTitleBlink();
      }, duration);
    }

    if (!state.blinkVisibilityHandler) {
      state.blinkVisibilityHandler = () => {
        if (!document.hidden && state.isTitleBlinking) {
          this.stopTitleBlink();
        }
      };
      document.addEventListener('visibilitychange', state.blinkVisibilityHandler);
    }

    return { titleBlinking: true };
  },

  /**
   * Stop title blinking and restore the previous title.
   * Re-applies identify titles when that mode is enabled.
   */
  stopTitleBlink() {
    const state = this.state;
    const wasBlinking = state.isTitleBlinking;

    if (state.blinkInterval) {
      window.clearInterval(state.blinkInterval);
      state.blinkInterval = null;
    }

    if (state.blinkDurationTimer) {
      window.clearTimeout(state.blinkDurationTimer);
      state.blinkDurationTimer = null;
    }

    if (wasBlinking && state.blinkOriginalTitle != null) {
      document.title = state.blinkOriginalTitle;
    }

    state.isTitleBlinking = false;
    state.blinkOriginalTitle = null;
    state.blinkIdleTitle = null;

    if (wasBlinking && this.isIdentifyEnabled()) {
      this.applyIdentify(true);
    }
  },

  /**
   * Peer handler for `identify`.
   *
   * @param {{ enabled: boolean }} [payload]
   */
  onIdentify(payload = {}) {
    this.applyIdentify(!!payload.enabled);
  },

  /**
   * Peer handler for `activate`.
   *
   * @param {{
   *   tabId: string,
   *   title?: string,
   *   message?: string,
   *   requestNonce: string
   * }} [payload]
   */
  onActivate(payload = {}) {
    if (payload.tabId !== tabIdentity.peekTabId()) {
      return;
    }

    const title = payload.title || '';
    const blink = this.blinkTitle(title);
    const status = Object.assign(
      { titleBlinking: blink.titleBlinking },
      notification.notify(title, {
        body: payload.message || '',
        onClick: () => window.focus(),
        onClose: () => this.stopTitleBlink(),
        onError: () => this.stopTitleBlink()
      }, () => {
        window.focus();
        this.stopTitleBlink();
      })
    );

    sharedStorage.tabChannel.publish('activate-ack', {
      tabId: tabIdentity.peekTabId(),
      requestNonce: payload.requestNonce,
      status: status
    });
  },

  /**
   * Peer handler for `activate-ack`.
   *
   * @param {{
   *   tabId: string,
   *   requestNonce: string,
   *   status?: { titleBlinking?: boolean }|null
   * }} [payload]
   */
  onActivateAck(payload = {}) {
    if (!payload.requestNonce) {
      return;
    }

    const resolve = this.state.pendingActivates[payload.requestNonce];

    if (resolve) {
      delete this.state.pendingActivates[payload.requestNonce];
      resolve({
        delivered: true,
        status: payload.status || null
      });
    }
  },

  /**
   * Peer handler for `blink`.
   *
   * @param {{
   *   tabId: string,
   *   options?: {
   *     title?: string,
   *     idleTitle?: string,
   *     prefix?: string,
   *     suffix?: string,
   *     intervalMs?: number,
   *     duration?: number,
   *     count?: number,
   *     timeout?: number
   *   },
   *   requestNonce: string
   * }} [payload]
   */
  onBlink(payload = {}) {
    if (payload.tabId !== tabIdentity.peekTabId()) {
      return;
    }

    const { title, ...blinkOptions } = payload.options || {};

    sharedStorage.tabChannel.publish('blink-ack', {
      tabId: tabIdentity.peekTabId(),
      requestNonce: payload.requestNonce,
      status: this.blinkTitle(title, blinkOptions)
    });
  },

  /**
   * Peer handler for `blink-ack`.
   *
   * @param {{
   *   tabId: string,
   *   requestNonce: string,
   *   status?: { titleBlinking: boolean }|null
   * }} [payload]
   */
  onBlinkAck(payload = {}) {
    if (!payload.requestNonce) {
      return;
    }

    const resolve = this.state.pendingBlinks[payload.requestNonce];

    if (resolve) {
      delete this.state.pendingBlinks[payload.requestNonce];
      resolve({
        delivered: true,
        status: payload.status || null
      });
    }
  },

  /**
   * Apply or restore this tab's document.title.
   *
   * @param {boolean} enabled
   */
  applyIdentify(enabled) {
    const state = this.state;

    state.identifyEnabled = !!enabled;

    // Avoid fighting title blink; blink restore re-applies identify when done.
    if (this.isTitleBlinking()) {
      return;
    }

    if (enabled) {
      if (state.originalTitle === null) {
        state.originalTitle = document.title;
      }

      document.title = tabIdentity.peekTabId() || state.originalTitle;
      return;
    }

    if (state.originalTitle !== null) {
      document.title = state.originalTitle;
      state.originalTitle = null;
    }
  }
};
